import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styles from "./WorkspaceHeader.module.scss";
import { RxExternalLink } from "react-icons/rx";
import { IoMdArrowDropdown } from "react-icons/io";
import { getWorkspaceNameFromUrl } from "@/entities/WorkSpace/api/getWorkspaceId";
import axios from "@/shared/api/axios";
import { getCompanyData } from "@/entities/WorkSpace/api/getCompanyData";
import { Company } from "@/entities/WorkSpace/api/companyList";
import { useSelector } from "react-redux";
import { RootState } from "@/app/appStore";
import { showToast } from "@utils/GlobalUtil";

interface WorkspaceTab {
  id: string;
  label: string;
  path?: string;
  icon?: React.ReactNode;
  isDropdown?: boolean;
  dropdownItems?: { id: string; label: string; path: string }[];
}

export const WorkspaceHeader = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);

  // Redux store에서 현재 선택된 회사 정보 가져오기
  const { primaryCompany } = useSelector((state: RootState) => state.layoutHeader);

  const [tabs] = useState<WorkspaceTab[]>([
    { id: "overview", label: "개요", path: `/${userId}/overview` },
    // {
    //   id: "company", label: "회사관리", icon: <IoMdArrowDropdown />,
    //   isDropdown: true,
    //   dropdownItems: [
    //     { id: "item1", label: "회사정보관리", path: `/${userId}/companymanagement` },
    //     { id: "item2", label: "수집정보관리", path: `/${userId}/datacollectionmanagement` },
    //   ]
    // },
    { id: "company-management", label: "회사관리", path: `/${userId}/company-management` },
    { id: "collection-management", label: "수집정보관리", path: `/${userId}/collection-management` },
    { id: "tax-invoice", label: "회계관리", path: `/${userId}/accountingmanagement`, icon: <RxExternalLink /> },
  ]);

  useEffect(() => {
    const currentPath = window.location.pathname;
    const currentTab = tabs.find(tab => {
      if (tab.isDropdown && tab.dropdownItems) {
        return tab.dropdownItems.some(item => {
          const itemPathPattern = item.path.replace(':userId', userId || '');
          return itemPathPattern === currentPath;
        });
      }
      // 동적 라우팅 경로를 실제 userId로 치환해서 비교
      const tabPathPattern = tab.path?.replace(':userId', userId || '');
      return tabPathPattern === currentPath;
    });

    if (currentTab) {
      setActiveTab(currentTab.id);
    }
  }, [tabs, userId, window.location.pathname]);

  const handleTabClick = async (tab: WorkspaceTab) => {
    if (tab.id === 'tax-invoice') {
      try {
        const workspaceName = getWorkspaceNameFromUrl();
        const response = await axios.get(`/v1/hub/workspaces/${workspaceName}`);
        const companyId = response.data.data.primaryCompany.companyId;

        if (!companyId) {
          showToast('회사 정보를 찾을 수 없습니다.', 'error');
          return;
        }

        const companyResponse = await getCompanyData(companyId);

        if (!companyResponse.success) {
          showToast('회사 정보를 가져오는데 실패했습니다.', 'error');
          return;
        }

        const queryParams = new URLSearchParams({
          cId: companyId,
          period: companyResponse.data.accountingPeriod.period,
          dateRange: (companyResponse.data.accountingPeriod.startDate + companyResponse.data.accountingPeriod.endDate).replace(/-/g, '')
        }).toString();

        window.open(`https://www.xaspace.ai/main?${queryParams}`, '_blank', 'noopener,noreferrer');
      } catch (error) {
        console.error('회사 정보를 가져오는데 실패했습니다:', error);
        showToast('회사 정보를 가져오는데 실패했습니다.', 'error');
      }
    } else if (tab.path) {
      setActiveTab(tab.id);

      // 회사관리와 수집정보관리 탭에 cId 쿼리 파라미터 추가
      if ((tab.id === 'company-management' || tab.id === 'collection-management') && primaryCompany?.companyId) {
        const pathWithQuery = `${tab.path}?cId=${primaryCompany.companyId}`;
        navigate(pathWithQuery);
      } else {
        navigate(tab.path);
      }
    }
  };

  const handleMouseEnter = (tabId: string) => {
    setOpenDropdown(tabId);
  };

  const handleMouseLeave = () => {
    setOpenDropdown(null);
  };

  const isActiveTab = (tab: WorkspaceTab) => {
    const currentPath = window.location.pathname;
    if (tab.isDropdown && tab.dropdownItems) {
      return tab.dropdownItems.some(item => {
        const itemPathPattern = item.path.replace(':userId', userId || '');
        return itemPathPattern === currentPath;
      }) || activeTab === tab.id;
    }
    const tabPathPattern = tab.path?.replace(':userId', userId || '');
    return tabPathPattern === currentPath || activeTab === tab.id;
  };

  return (
    <div className={styles.userProfileContainer}>
      <div className={styles.tabContainer}>
        {tabs.map((tab: WorkspaceTab) => (
          <div
            className={`${styles.tabItem} ${isActiveTab(tab) ? styles.active : ""}`}
            key={tab.id}
            onClick={() => handleTabClick(tab)}
            onMouseEnter={() => tab.isDropdown && handleMouseEnter(tab.id)}
            onMouseLeave={handleMouseLeave}
          >
            {tab.label}
            {tab.icon && <span className={styles.icon}>{tab.icon}</span>}
            {tab.isDropdown && openDropdown === tab.id && (
              <div className={styles.dropdownMenu}>
                {tab.dropdownItems?.map((item) => (
                  <div
                    key={item.id}
                    className={styles.dropdownItem}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(item.path);
                    }}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
